<template>
  <div>
    <!-- Desktop View -->
    <page-toolbar v-if="!device.isMobile" :searchable="false" :loading="loading">
      <template #filters>
        <slot></slot>
        <v-col cols="auto">
          <dropdown
            hide-details
            :items="[
              { id: 1, name: $t('one_column') },
              { id: 2, name: $t('two_columns') },
              { id: 3, name: $t('one_sheet_one_user') },
            ]"
            item-value="id"
            v-model="card.type"
            item-text="name"
            :label="$t('type')"
            color="white"
            style="min-width: 180px"
          />
        </v-col>

        <v-col cols="auto">
          <dropdown
            hide-details
            :items="[
              { id: 1, name: 'ID' },
              { id: 2, name: $t('name') },
              { id: 3, name: $t('email') },
              { id: 4, name: $t('access_card_id') },
            ]"
            item-value="id"
            multiple
            v-model="card.visible_fields"
            item-text="name"
            @change="$emit('fieldsUpdated', card.visible_fields)"
            :label="$t('show_display')"
            color="white"
            chips
            small-chips
            style="min-width: 250px"
          />
        </v-col>

        <v-col cols="auto" style="max-width: 120px">
          <text-field
            v-model="card.width"
            :label="$t('width')"
            hide-details
            suffix="mm"
            color="white"
          />
        </v-col>

        <v-col cols="auto" style="max-width: 120px">
          <text-field
            v-model="card.height"
            :label="$t('height')"
            hide-details
            suffix="mm"
            color="white"
          />
        </v-col>

        <v-col cols="auto" style="max-width: 140px">
          <text-field
            v-model="card.marginTop"
            :label="$t('margin_top')"
            hide-details
            suffix="px"
            color="white"
            prepend-inner-icon="mdi-refresh"
            @click:prepend-inner="card.marginTop = 'auto'"
          />
        </v-col>

        <v-col cols="auto" style="max-width: 140px">
          <text-field
            v-model="card.fontSize"
            :label="$t('font_size')"
            hide-details
            suffix="px"
            color="white"
          />
        </v-col>
        <v-col cols="auto" v-if="showPrintSettings">
          <autocomplete
            v-model="selectedEmployees"
            :items="employees"
            multiple
            :label="$t('employees_for_qr_printing')"
            return-object
            item-text="email"
            item-value="id"
            hide-details
            color="white"
            chips
            small-chips
            select-all
            style="width: 250px"
          >
          </autocomplete>
        </v-col>

        <v-col v-if="!hidePrintButton">
          <v-row no-gutters justify="end" align="center">
            <primary-button
              v-if="biggerButton"
              @click="print"
              :loading="loading"
              height="40px"
            >
              <v-icon class="mr-2">mdi-qrcode</v-icon>{{ $t("qr") }}
            </primary-button>
            <print-button
              height="40px"
              v-else
              @click="print"
              :loading="loading"
            ></print-button>
            <slot name="additional-actions"></slot>
          </v-row>
        </v-col>
        <slot name="additional-filters"></slot>
      </template>
    </page-toolbar>

    <!-- Mobile View -->
    <template v-else>
      <mobile-filters
        :filters="mobileFilterConfig"
        @update:filter="handleMobileFilterUpdate"
        @apply="onFiltersApply"
        @reset="resetFilters"
      >
        <template #filter-type>
          <dropdown
            hide-details
            :items="[
              { id: 1, name: $t('one_column') },
              { id: 2, name: $t('two_columns') },
              { id: 3, name: $t('one_sheet_one_user') },
            ]"
            item-value="id"
            v-model="card.type"
            item-text="name"
            :label="$t('type')"
          />
        </template>
        <template #filter-visibleFields>
          <dropdown
            hide-details
            :items="[
              { id: 1, name: 'ID' },
              { id: 2, name: $t('name') },
              { id: 3, name: $t('email') },
              { id: 4, name: $t('access_card_id') },
            ]"
            item-value="id"
            multiple
            v-model="card.visible_fields"
            item-text="name"
            @change="$emit('fieldsUpdated', card.visible_fields)"
            :label="$t('show_display')"
            chips
            small-chips
          />
        </template>
        <template #filter-dimensions>
          <v-row dense>
            <v-col cols="6">
              <text-field
                v-model="card.width"
                :label="$t('width')"
                hide-details
                suffix="mm"
              />
            </v-col>
            <v-col cols="6">
              <text-field
                v-model="card.height"
                :label="$t('height')"
                hide-details
                suffix="mm"
              />
            </v-col>
            <v-col cols="6">
              <text-field
                v-model="card.marginTop"
                :label="$t('margin_top')"
                hide-details
                suffix="px"
              />
            </v-col>
            <v-col cols="6">
              <text-field
                v-model="card.fontSize"
                :label="$t('font_size')"
                hide-details
                suffix="px"
              />
            </v-col>
          </v-row>
        </template>
        <template #filter-employees v-if="showPrintSettings">
          <autocomplete
            v-model="selectedEmployees"
            :items="employees"
            multiple
            :label="$t('employees_for_qr_printing')"
            return-object
            item-text="email"
            item-value="id"
            hide-details
            chips
            small-chips
            select-all
          />
        </template>
      </mobile-filters>

      <div class="px-3 mt-2" v-if="!hidePrintButton">
        <v-btn color="primary" block @click="print" :loading="loading">
          <v-icon class="mr-2">mdi-printer</v-icon>
          {{ $t("print") }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import PageToolbar from "@/components/global/PageToolbar.vue";
import MobileFilters from "@/components/global/MobileFilters.vue";
import "@/mixins/Roboto-normal.js";
export default {
  components: {
    PageToolbar,
    MobileFilters,
  },
  props: {
    employees: {
      type: Array,
      required: true,
    },
    restaurantName: {
      type: String,
      required: false,
      default: "",
    },
    biggerButton: {
      type: Boolean,
      default: false,
    },
    showPrintSettings: {
      type: Boolean,
      default: false,
    },
    hidePrintButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      selectedEmployees: [],
      card: {
        width: "90",
        height: "50",
        marginTop: "25",
        fontSize: "13",
        type: 2,
        visible_fields: [1, 2, 3, 4],
        search: "",
      },
    };
  },
  computed: {
    mobileFilterConfig() {
      const typeNames = {
        1: this.$t("one_column"),
        2: this.$t("two_columns"),
        3: this.$t("one_sheet_one_user"),
      };

      const filters = {
        type: {
          label: this.$t("type"),
          value: this.card.type,
          chipText: typeNames[this.card.type] || "",
          type: "custom",
          visible: true,
          clearable: false,
        },
        visibleFields: {
          label: this.$t("show_display"),
          value: this.card.visible_fields,
          chipText: `${this.card.visible_fields.length} ${this.$t("views_selected")}`,
          type: "custom",
          visible: true,
          clearable: false,
        },
        dimensions: {
          label: this.$t("dimensions"),
          value: `${this.card.width}x${this.card.height}`,
          chipText: `${this.card.width}x${this.card.height}mm`,
          type: "custom",
          visible: true,
          clearable: false,
        },
      };

      if (this.showPrintSettings) {
        filters.employees = {
          label: this.$t("employees"),
          value: this.selectedEmployees,
          chipText: `${this.selectedEmployees.length} ${this.$t("employees_selected")}`,
          type: "custom",
          visible: true,
          clearable: false,
        };
      }

      return filters;
    },
    filteredEmployees() {
      if (!this.card.search || this.card.search.trim() === "") {
        return this.selectedEmployees;
      }

      const searchLower = this.card.search.toLowerCase();
      return this.selectedEmployees.filter((employee) => {
        // Search in ID
        if (employee.id && employee.id.toString().includes(searchLower)) {
          return true;
        }

        // Search in name
        if (employee.name && employee.name.toLowerCase().includes(searchLower)) {
          return true;
        }

        // Search in lastname
        if (employee.lastname && employee.lastname.toLowerCase().includes(searchLower)) {
          return true;
        }

        // Search in email
        if (employee.email && employee.email.toLowerCase().includes(searchLower)) {
          return true;
        }

        // Search in external code
        if (
          employee.external_code &&
          employee.external_code.toLowerCase().includes(searchLower)
        ) {
          return true;
        }

        return false;
      });
    },
  },
  watch: {
    employees: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0 && this.selectedEmployees.length === 0) {
          // Default: select all employees
          this.selectedEmployees = [...newVal];
        }
      },
    },
  },
  methods: {
    handleMobileFilterUpdate({ key, value }) {
      if (key === "type") {
        this.card.type = value;
      } else if (key === "visibleFields") {
        this.card.visible_fields = value;
        this.$emit("fieldsUpdated", value);
      } else if (key === "employees") {
        this.selectedEmployees = value;
      }
    },
    onFiltersApply() {
      // Filters are already applied via v-model
    },
    resetFilters() {
      this.card.type = 2;
      this.card.visible_fields = [1, 2, 3, 4];
      this.card.width = "90";
      this.card.height = "50";
      this.card.marginTop = "25";
      this.card.fontSize = "13";
      if (this.employees && this.employees.length > 0) {
        this.selectedEmployees = [...this.employees];
      }
    },
    removeEmployee(item) {
      const index = this.selectedEmployees.findIndex((e) => e.id === item.id);
      if (index > -1) {
        this.selectedEmployees.splice(index, 1);
      }
    },
    getEmployeeSvg(qrCodeElem) {
      if (qrCodeElem) {
        // For qrcode-vue, the SVG is the first child
        const svg = qrCodeElem.querySelector("svg");
        if (svg) {
          // Set SVG width/height from card settings
          svg.setAttribute("width", `100%`);
          svg.setAttribute("aspect-ratio", `1`);
          svg.setAttribute("height", "auto");
          svg.style.width = `100%`;
          svg.style.aspectRatio = `1`;
          svg.style.height = "auto";
          return svg.outerHTML;
        } else {
          // fallback: maybe the element itself is svg
          if (qrCodeElem.tagName.toLowerCase() === "svg") {
            qrCodeElem.setAttribute("width", `100%`);
            qrCodeElem.setAttribute("aspect-ratio", `1`);
            qrCodeElem.setAttribute("height", "auto");
            svg.style.width = `100%`;
            svg.style.aspectRatio = `1`;
            svg.style.height = "auto";
            return qrCodeElem.outerHTML;
          }
        }
      }
    },
    singleQrPrint(item, index) {
      let isTable = this.filteredEmployees.every(
        (i) => i.company && i.company.type === "table"
      );
      let column = isTable ? 4 : this.card.type == 2 ? 2 : 1;

      let html = "<table>";
      let i = 1;

      if (index % column === 0) {
        i = 1;
        html += "<tr>";
      }
      let employeeQr = document.getElementById("qrkodstampa" + item.id);
      let id = item.id;
      let name = this.toLatin([item.name, item.lastname].filter(Boolean).join(" "));
      let email = item.email || "";

      let style2 =
        index % column === 0
          ? "border-left: 1px solid rgba(0, 0, 0, 0.12); padding-right: 6px"
          : "padding-right: 6px";
      let canvas = employeeQr.getElementsByTagName("svg");

      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(canvas[0]);
      const encodedData = "data:image/svg+xml," + encodeURIComponent(svgString);
      html += '<td style="' + style2 + '">';
      html += `<img src="${encodedData}" style="padding-left: ${
        this.card.marginTop
      }px;width: ${this.card.width * 0.4}mm; height: ${this.card.width * 0.4}mm"/>`;
      if (isTable)
        html += `<div style="font-size: ${this.card.fontSize}px; text-align: center">${name}</div>`;
      html += "</td>";

      let style =
        index % column === 0
          ? "border-right: 1px solid rgba(0, 0, 0, 0.12);"
          : "border-right: 1px solid rgba(0, 0, 0, 0.12);";

      if (!isTable) {
        html += '<td style="' + style + '">';
        html += `<div style="margin-left: 10px;display: flex; flex-direction: column; height: ${
          this.card.height
        }mm; justify-content: center; width: ${this.card.width * 0.6}mm;">`;
        html += `<div style="height: ${
          this.card.marginTop === "auto"
            ? this.card.height * 0.4 - 15
            : this.card.height - this.card.marginTop
        }mm; line-height: 1.5">`;
        if (this.card.visible_fields.includes(1)) {
          html += `<div style="font-size: ${this.card.fontSize}px;color: #555">
            <strong>Ordera ID: ${id}</strong>
            </div>`;
        }
        if (this.card.visible_fields.includes(2)) {
          html += `<div style="font-size: ${this.card.fontSize}px; font-weight: 600">${name}</div>`;
        }
        if (this.card.visible_fields.includes(3)) {
          html += `<div style="font-size: ${this.card.fontSize}px; color: #555">${email}</div>`;
        }
        if (this.card.visible_fields.includes(4) && item.external_code) {
          html += `<div style="font-size: ${this.card.fontSize}px; color: #555">
            <strong>${this.$t("access_card_id")}: </strong>${item.external_code}
            </div>`;
        }
        html += `</div>`;
        html += `<div style="text-align: end"><span style="margin-right: 10px; color: #aaa">ordera.app</span> <img src="./o.svg" width="30px" style="margin-bottom: -4px; margin-right: ${this.card.marginTop}px"/></div>`;
        html += `</div>`;
        html += "</td>";
      }

      if (i === column) html += "</tr>";
      i++;
      html += "</table>";

      html = [
        "<!DOCTYPE html>" + "<html>",
        "   <head>",
        "   <style>td{border-bottom: 1px solid rgba(0, 0, 0, 0.12);}</style>",
        "   </head>",
        '   <body onload="window.print()" onafterprint="window.close()" style="font-family: Roboto, sans-serif; font-size: ' +
          (this.card.fontSize - 2) +
          'px!important;">',
        html,
        "   </body>",
        "</html>",
      ].join("");
      let win = window.open("", "", "left=0,top=0,toolbar=0,scrollbars=0,status=0");
      win.document.open();
      win.document.write(html);
      win.document.close();
    },
    printSinglePage() {
      this.loading = true;
      const width = Number(this.card.width);
      const height = Number(this.card.height);
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: [width, height],
      });
      doc.setFont("Roboto", "normal");
      const addEmployeePage = (employee, isFirst) => {
        let id = employee.id;
        let email = employee.email || "";
        let qrCodeElem = document.getElementById("qrkodstampa" + id);
        let svg = qrCodeElem ? qrCodeElem.querySelector("svg") : null;
        if (
          !svg &&
          qrCodeElem &&
          qrCodeElem.tagName &&
          qrCodeElem.tagName.toLowerCase() === "svg"
        ) {
          svg = qrCodeElem;
        }
        if (!svg) return;

        // Convert SVG to PNG using html2canvas
        // Create a temporary container for html2canvas
        let tempDiv = document.createElement("div");
        tempDiv.style.position = "fixed";
        tempDiv.style.left = "-9999px";
        tempDiv.appendChild(svg.cloneNode(true));
        document.body.appendChild(tempDiv);

        // Use html2canvas to render SVG to image
        // html2canvas returns a Promise
        // We'll collect all promises and then save the PDF after all are done
        return html2canvas(tempDiv, {
          backgroundColor: null,
          useCORS: true,
          scale: 3,
        }).then((canvas) => {
          const imgData = canvas.toDataURL("image/png");
          document.body.removeChild(tempDiv);
          if (!isFirst) doc.addPage([width, height], "landscape");
          if (this.card.visible_fields.length < 3) {
            const qrSize = height * 0.6;
            doc.addImage(imgData, "PNG", 2, height / 2 - qrSize / 2, qrSize, qrSize);
            const rightColumnSize = width - qrSize - 10; // 10mm margin
            const fontSize = Math.floor(rightColumnSize / 2);
            const smallFontSize = Math.floor(rightColumnSize / 3);
            let offsetY = 4 + height / 2 - qrSize / 2;
            let lineHeight = Math.floor(Number(fontSize) / 2);
            doc.setFontSize(Number(fontSize));
            if (this.card.visible_fields.includes(2)) {
              doc.text(this.toLatin(employee.name), qrSize + 4, offsetY);
              offsetY += lineHeight;
              doc.text(this.toLatin(employee.lastname), qrSize + 4, offsetY);
              offsetY += lineHeight;
            }
            doc.setFontSize(Number(smallFontSize));
            if (this.card.visible_fields.includes(3)) {
              doc.text(email, qrSize + 4, offsetY);
              offsetY += lineHeight;
            }
            if (this.card.visible_fields.includes(1)) {
              doc.text(`Ordera ID: ${id}`, qrSize + 4, offsetY);
              offsetY += lineHeight;
            }
            if (this.card.visible_fields.includes(4) && employee.external_code) {
              doc.text(
                `${this.$t("access_card_id")}: ${employee.external_code}`,
                qrSize + 4,
                offsetY
              );
            }
          } else {
            const qrSize = height * 0.4;
            const rightColumnSize = width - qrSize - 10; // 10mm margin
            const fontSize = Math.floor(rightColumnSize / 3);
            const smallFontSize = Math.floor(rightColumnSize / 4);
            const offsetQr = (width - qrSize) / 2;
            let offsetY = 2;
            doc.addImage(imgData, "PNG", offsetQr, offsetY, qrSize, qrSize);
            let lineHeight = Math.floor(Number(fontSize) / 2);
            offsetY += qrSize + 3;
            const centerX = width / 2;
            doc.setFontSize(Number(fontSize));
            if (this.card.visible_fields.includes(2)) {
              doc.text(
                [employee.name, employee.lastname].map(this.toLatin).join(" "),
                centerX,
                offsetY,
                {
                  align: "center",
                }
              );
              offsetY += lineHeight;
            }
            doc.setFontSize(Number(smallFontSize));
            if (this.card.visible_fields.includes(3)) {
              doc.text(email, centerX, offsetY, {
                align: "center",
              });
              offsetY += lineHeight;
            }
            if (this.card.visible_fields.includes(1)) {
              doc.text(`Ordera ID: ${id}`, centerX, offsetY, {
                align: "center",
              });
              offsetY += lineHeight;
            }
            if (this.card.visible_fields.includes(4) && employee.external_code) {
              doc.text(
                `${this.$t("access_card_id")}: ${employee.external_code}`,
                centerX,
                offsetY,
                { align: "center" }
              );
            }
          }
        });
      };

      // Collect all html2canvas promises
      let promises = this.filteredEmployees.map((employee, idx) =>
        addEmployeePage(employee, idx === 0)
      );
      Promise.all(promises)
        .then(() => {
          // Open PDF in new window/tab instead of downloading
          const pdfUrl = doc.output("bloburl");
          window.open(pdfUrl, "_blank");
        })
        .catch((err) => {
          // Optionally, show error to user
          console.error("PDF generation error", err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    print() {
      if (this.card.type === 3) {
        this.loading = true;
        setTimeout(() => {
          this.printSinglePage();
        }, 100);
        return;
      }
      let isTable = this.filteredEmployees.every(
        (i) => i.company && i.company.type === "table"
      );
      let column = isTable ? 4 : this.card.type == 2 ? 2 : 1;

      let html = "<table>";
      let i = 1;
      this.filteredEmployees.forEach((employee, index) => {
        if (index % column === 0) {
          i = 1;
          html += "<tr>";
        }

        let id = employee.id;
        let name = [this.toLatin(employee.name), this.toLatin(employee.lastname)]
          .filter(Boolean)
          .join(" ");
        let email = employee.email || "";
        let style2 =
          index % column === 0
            ? "border-left: 1px solid rgba(0, 0, 0, 0.12); padding-right: 6px"
            : "padding-right: 6px";

        let canvas = "";
        let qrCodeElem = document.getElementById("qrkodstampa" + employee.id);
        if (qrCodeElem) {
          // For qrcode-vue, the SVG is the first child
          const svg = qrCodeElem.querySelector("svg");
          if (svg) {
            // Set SVG width/height from card settings
            svg.setAttribute("width", `${this.card.width * 0.4}mm`);
            svg.setAttribute("height", `${this.card.height}mm`);
            svg.style.width = `${this.card.width * 0.4}mm`;
            svg.style.height = `${this.card.height}mm`;
            canvas = svg.outerHTML;
          } else {
            // fallback: maybe the element itself is svg
            if (qrCodeElem.tagName.toLowerCase() === "svg") {
              qrCodeElem.setAttribute("width", `${this.card.width * 0.4}mm`);
              qrCodeElem.setAttribute("height", `${this.card.height}mm`);
              svg.style.width = `${this.card.width * 0.4}mm`;
              svg.style.height = `${this.card.height}mm`;
              canvas = qrCodeElem.outerHTML;
            }
          }
        }
        html += '<td style="' + style2 + '">';
        html += `<div style="padding-left: ${this.card.marginTop}px;width: ${
          this.card.width * 0.4
        }mm; height: ${this.card.height}mm">${qrCodeElem?.innerHTML}</div>`;
        if (isTable)
          html += `<div style="font-size: ${this.card.fontSize}px; text-align: center">${name}</div>`;
        html += "</td>";

        let style =
          index % column === 0
            ? "border-right: 1px solid rgba(0, 0, 0, 0.12);"
            : "border-right: 1px solid rgba(0, 0, 0, 0.12);";

        if (!isTable) {
          html += '<td style="' + style + '">';
          html += `<div style="display: flex; flex-direction: column; height: ${
            this.card.height
          }mm; justify-content: center; width: ${this.card.width * 0.6}mm">`;
          html += `<div style="height: ${
            this.card.marginTop === "auto"
              ? this.card.height * 0.4 - 15
              : this.card.height - this.card.marginTop
          }mm; line-height: 1.5">`;
          if (this.card.visible_fields.includes(2)) {
            html += `<div style="font-size: ${this.card.fontSize}px; font-weight: 600">${name}</div>`;
          }
          if (this.card.visible_fields.includes(3)) {
            html += `<div style="font-size: ${this.card.fontSize}px; color: #555">${email}</div>`;
          }
          if (this.card.visible_fields.includes(1)) {
            html += `<div style="font-size: ${this.card.fontSize}px;color: #555">
              <strong>Ordera ID: ${id}</strong>
              </div>`;
          }
          if (this.card.visible_fields.includes(4) && employee.external_code) {
            html += `<div style="font-size: ${this.card.fontSize}px; color: #555">
              <strong>${this.$t("access_card_id")}: </strong>${employee.external_code}
              </div>`;
          }
          html += `</div>`;
          html += `<div style="text-align: end"><span style="margin-right: 10px; color: #aaa">ordera.app</span> <img src="./o.svg" width="30px" style="margin-bottom: -4px;margin-right: ${this.card.marginTop}px"/></div>`;
          html += `</div>`;
          html += "</td>";
        }
        if (i === column) html += "</tr>";
        i++;
      });
      html += "</table>";
      //  onafterprint="window.close()"
      html = [
        "<!DOCTYPE html>" + "<html>",
        "   <head>",
        "   <style>td{border-bottom: 1px solid rgba(0, 0, 0, 0.12);}" +
          "    table { page-break-inside:auto }\n" +
          "    tr    { page-break-inside:avoid; page-break-after:auto }\n" +
          "    thead { display:table-header-group }\n" +
          "    tfoot { display:table-footer-group }</style>",
        "   </head>",
        '   <body onload="window.print()" style="font-family: Roboto, sans-serif; font-size: ' +
          (this.card.fontSize - 2) +
          'px!important;">',
        html,
        "   </body>",
        "</html>",
      ].join("");
      let win = window.open("", "", "left=0,top=0,toolbar=0,scrollbars=0,status=0");
      win.document.open();
      win.document.write(html);
      win.document.close();
    },
  },
  watch: {
    employees: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0 && this.selectedEmployees.length === 0) {
          // Default: select all employees
          this.selectedEmployees = [...newVal];
        }
      },
    },
    card: {
      handler(newVal) {
        const cardCopy = { ...newVal };
        delete cardCopy.search; // Don't emit search to avoid loop
        this.$emit("cardUpdated", cardCopy);
      },
      deep: true,
    },
  },
};
</script>

<style>
@page {
  size: A3;
  margin: 0;
}
@media print {
  html,
  body {
    width: 210mm;
    height: 297mm;
  }
}
</style>
